import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import posthog from "posthog-js";
import { Button, Icon, Confirm, Message, Label, Popup } from "semantic-ui-react";
import CommonPageHeader from "../../../ui/page-header/CommonPageHeader";
import SearchInput from "../../../ui/search-input/SearchInput";
import SelectFilter from "../../../ui/select-filter/SelectFilter";
import ExcelExportButton from "../../../ui/excel-export-button/ExcelExportButton";

import * as projectActions from "../../../../domain/project/projectActions";
import {
  displayPurposeFromNotes,
  getProjectDisplayTitle,
  getProjectDisplayDescription,
  getTimelinePercentFromRow,
  getProjectStatusPresentation,
  getProjectStatusFilterKey,
  getProjectStatusSortIndex,
  getPriorityPresentation,
  getProjectAssociatedCount,
} from "./projectFormMeta";
import { Common } from "../../../../../utils";
import { useSubscriptionStatus } from "../../../../../hooks/useSubscriptionStatus";
import SubscriptionRestriction from "../../../subscriptionRestriction/SubscriptionRestriction";
import "../departments/department.css";
import "../../../../domain/documents/Documents.css";
import "./projects-grid.css";

const DEFAULT_PROJECT_CARD_ICON = "briefcase";

const PROJECTS_VIEW_STORAGE_KEY = "hrm-settings-projects-view";

/** API page size for Project/…/start/page/take — keep in sync with projectActions default take. */
const API_PAGE_SIZE = 10;

function formatProjectCreatedDate(row) {
  const raw = row?.createdDate ?? row?.CreatedDate;
  if (!raw) return null;
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

function computeListExhausted(baseModel, take, previousLoadedCount) {
  if (!baseModel?.success) return true;
  const chunk = Array.isArray(baseModel.data) ? baseModel.data.length : 0;
  if (chunk === 0) return true;
  const total = baseModel.total;
  const loadedAfter = previousLoadedCount + chunk;
  if (typeof total === "number" && loadedAfter >= total) return true;
  if (chunk < take) return true;
  return false;
}

const Project = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const projectReducer = useSelector((state) => state.projectReducer);
  const { isSuspended } = useSubscriptionStatus();
  const history = useHistory();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deletingItem, setDeletingItem] = useState(null);
  const [sortKey, setSortKey] = useState("name");
  const [sortDir, setSortDir] = useState("asc");
  const [searchInput, setSearchInput] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [exportLoading, setExportLoading] = useState(false);
  const [serverTotal, setServerTotal] = useState(null);
  const [listExhausted, setListExhausted] = useState(false);
  const [viewMode, setViewMode] = useState(() => {
    try {
      const v = sessionStorage.getItem(PROJECTS_VIEW_STORAGE_KEY);
      return v === "list" ? "list" : "grid";
    } catch {
      return "grid";
    }
  });
  const lastFetchedPageRef = useRef(0);
  const scrollSentinelRef = useRef(null);

  const { record, isLoading, isLoadingMore, Error, message } = projectReducer;
  const totalItems = Array.isArray(record) ? record.length : 0;
  const displayTotalCount = serverTotal != null ? serverTotal : totalItems;

  const statusFilterOptions = useMemo(
    () => [
      { value: "all", label: t("app.projects.filterStatusAll") },
      { value: "planning", label: t("app.projects.workflowStatusPlanning") },
      { value: "review", label: t("app.projects.workflowStatusReview") },
      { value: "onHold", label: t("app.projects.workflowStatusOnHold") },
      { value: "inProgress", label: t("app.projects.workflowStatusInProgress") },
      { value: "completed", label: t("app.projects.workflowStatusCompleted") },
      { value: "inactive", label: t("app.projects.workflowStatusInactive") },
      { value: "deactivated", label: t("app.projects.statusDeactivated") },
    ],
    [t]
  );

  const sortOptions = useMemo(
    () => [
      { value: "name::asc", label: t("app.projects.sortNameAsc") },
      { value: "name::desc", label: t("app.projects.sortNameDesc") },
      { value: "createdDate::desc", label: t("app.projects.sortDateNewest") },
      { value: "createdDate::asc", label: t("app.projects.sortDateOldest") },
      { value: "status::asc", label: t("app.projects.sortStatus") },
      { value: "userCount::desc", label: t("app.projects.sortEmployeesHigh") },
      { value: "userCount::asc", label: t("app.projects.sortEmployeesLow") },
    ],
    [t]
  );

  const sortValue = `${sortKey}::${sortDir}`;

  const filteredItems = useMemo(() => {
    const list = Array.isArray(record) ? record : [];
    const q = searchInput.trim().toLowerCase();
    return list.filter((item) => {
      const title = getProjectDisplayTitle(item);
      const purpose = displayPurposeFromNotes(item.notes);
      const desc = getProjectDisplayDescription(item);
      const matchesSearch =
        q.length === 0 ||
        String(title || "").toLowerCase().includes(q) ||
        String(item.name || "").toLowerCase().includes(q) ||
        String(purpose || "").toLowerCase().includes(q) ||
        String(desc || "").toLowerCase().includes(q) ||
        String(item.notes || "").toLowerCase().includes(q);
      const statusKey = getProjectStatusFilterKey(item);
      const matchesStatus = statusFilter === "all" || statusKey === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [record, searchInput, statusFilter]);

  const totalFiltered = filteredItems.length;
  const hasClientFilter =
    searchInput.trim() !== "" || statusFilter !== "all";
  const paginationSummary =
    totalFiltered <= 0
      ? t("app.projects.noResults")
      : hasClientFilter
        ? t("app.projects.paginationFilteredCounts", {
            matching: totalFiltered,
            loaded: totalItems,
            total: serverTotal != null ? serverTotal : "—",
          })
        : t("app.projects.paginationLoadedOfTotal", {
            loaded: totalItems,
            total: serverTotal != null ? serverTotal : totalItems,
          });

  const sortedItems = useMemo(() => {
    const list = [...filteredItems];
    const direction = sortDir === "asc" ? 1 : -1;
    const valueForSort = (item) => {
      switch (sortKey) {
        case "name":
          return String(getProjectDisplayTitle(item) || item.name || "").toLowerCase();
        case "createdDate": {
          const ts = item.createdDate ? new Date(item.createdDate).getTime() : 0;
          return Number.isNaN(ts) ? 0 : ts;
        }
        case "status":
          return getProjectStatusSortIndex(item);
        case "userCount":
          return getProjectAssociatedCount(item);
        default:
          return String(getProjectDisplayTitle(item) || item.name || "").toLowerCase();
      }
    };
    list.sort((a, b) => {
      const va = valueForSort(a);
      const vb = valueForSort(b);
      if (va < vb) return -1 * direction;
      if (va > vb) return 1 * direction;
      return 0;
    });
    return list;
  }, [filteredItems, sortKey, sortDir]);

  const loadedStatusBreakdown = useMemo(() => {
    const list = Array.isArray(record) ? record : [];
    const o = {
      inProgress: 0,
      completed: 0,
      onHold: 0,
      deactivated: 0,
      other: 0,
    };
    for (const row of list) {
      const k = getProjectStatusFilterKey(row);
      if (k === "deactivated") o.deactivated += 1;
      else if (k === "inProgress") o.inProgress += 1;
      else if (k === "completed") o.completed += 1;
      else if (k === "onHold") o.onHold += 1;
      else if (k === "planning" || k === "review" || k === "inactive") o.other += 1;
    }
    return o;
  }, [record]);

  useEffect(() => {
    try {
      sessionStorage.setItem(PROJECTS_VIEW_STORAGE_KEY, viewMode);
    } catch {
      /* ignore */
    }
  }, [viewMode]);

  useEffect(() => {
    if (isSuspended) history.replace("/settings/company?tab=billing");
  }, [isSuspended, history]);

  useEffect(() => {
    if (isSuspended) return;
    lastFetchedPageRef.current = 0;
    setServerTotal(null);
    setListExhausted(false);
    dispatch(
      projectActions.ProjectListing({ start: 0, page: 1, take: API_PAGE_SIZE }, 1, { append: false })
    )
      .then((bm) => {
        if (bm?.success) {
          lastFetchedPageRef.current = 1;
          if (typeof bm.total === "number") setServerTotal(bm.total);
          setListExhausted(computeListExhausted(bm, API_PAGE_SIZE, 0));
        }
      })
      .catch(() => {});
  }, [isSuspended, dispatch]);

  const loadMore = useCallback(() => {
    if (isLoading || isLoadingMore || listExhausted) return;
    const last = lastFetchedPageRef.current;
    if (last < 1) return;
    const page = last + 1;
    const start = last * API_PAGE_SIZE;
    const prevLen = Array.isArray(record) ? record.length : 0;
    dispatch(
      projectActions.ProjectListing({ start, page, take: API_PAGE_SIZE }, page, { append: true })
    )
      .then((bm) => {
        if (bm?.success) {
          lastFetchedPageRef.current = page;
          if (typeof bm.total === "number") setServerTotal(bm.total);
          setListExhausted(computeListExhausted(bm, API_PAGE_SIZE, prevLen));
        }
      })
      .catch(() => {});
  }, [isLoading, isLoadingMore, listExhausted, record, dispatch]);

  useEffect(() => {
    if (isSuspended || isLoading) return;
    const el = scrollSentinelRef.current;
    if (!el || listExhausted) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries[0]?.isIntersecting) return;
        loadMore();
      },
      { root: null, rootMargin: "240px", threshold: 0 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [isSuspended, isLoading, listExhausted, loadMore]);

  if (isSuspended) {
    return (
      <div style={{ padding: "20px" }}>
        <SubscriptionRestriction actionKey="app.subscriptionRestriction.actionAccessSettings" />
      </div>
    );
  }

  const openCreatePage = () => {
    history.push("/settings/organization/projects/new");
  };

  const handleEdit = (row) => {
    const pid = row.id ?? row.Id;
    if (pid == null || pid === "") return;
    history.push({
      pathname: "/settings/organization/projects/edit",
      state: { projectId: pid },
    });
  };

  const handleDelete = async (row) => {
    try {
      const result = await dispatch(projectActions.deleteProject(row));
      if (result?.success) Common.showSuccessMessage(result.message || t("app.projects.successDeactivated"));
      else Common.showErrorMessage(result?.message || t("app.projects.errorDeactivate"));
    } catch (error) {
      const apiMsg = error?.response?.data?.message ?? error?.response?.data?.Message ?? error?.message;
      Common.showErrorMessage(typeof apiMsg === "string" && apiMsg ? apiMsg : t("app.projects.errorDeactivate"));
    }
  };

  const handleReactivate = async (row) => {
    try {
      await dispatch(projectActions.patchProject({ ...row, isActive: true }));
      Common.showSuccessMessage(t("app.projects.successReactivated"));
    } catch (error) {
      if (typeof posthog !== "undefined" && posthog.capture) {
        posthog.capture("projectReactivate", {
          type: "exception",
          success: false,
          reason: error?.response?.data?.message ?? error?.message ?? "Request failed",
          status: error?.response?.status,
        });
      }
      Common.showErrorMessage(t("app.projects.errorReactivate"));
    }
  };

  const handleSortChange = (next) => {
    const parts = String(next).split("::");
    if (parts.length === 2) {
      setSortKey(parts[0]);
      setSortDir(parts[1]);
    }
  };

  const handleSummaryStatusFilter = (filterKey) => {
    setStatusFilter((prev) => (prev === filterKey ? "all" : filterKey));
  };

  const handleExport = () => {
    try {
      setExportLoading(true);
      const rows = [
        ["Name", "Description", "Created Date", "Status", "Associated Employees"],
        ...sortedItems.map((d) => [
          getProjectDisplayTitle(d) || d.name || "",
          getProjectDisplayDescription(d) || "",
          d.createdDate ? new Date(d.createdDate).toLocaleDateString() : "N/A",
          getProjectStatusPresentation(d, t).label,
          String(getProjectAssociatedCount(d)),
        ]),
      ];
      const csv = rows.map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(",")).join("\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "projects.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } finally {
      setExportLoading(false);
    }
  };

  const renderProjectActions = (row, stop) => (
    <div className="project-card__actions">
      <Popup
        content={t("app.projects.editItem")}
        trigger={
          <button
            type="button"
            className="project-card__action-btn"
            onClick={(e) => {
              stop(e);
              handleEdit(row);
            }}
            aria-label={t("app.projects.editItem")}
          >
            <Icon name="edit outline" />
          </button>
        }
      />
      {row.isActive ? (
        <Popup
          content={t("app.projects.deactivateItem")}
          trigger={
            <button
              type="button"
              className="project-card__action-btn project-card__action-btn--danger"
              onClick={(e) => {
                stop(e);
                setDeletingItem(row);
                setIsDeleteModalOpen(true);
              }}
              aria-label={t("app.projects.deactivateItem")}
            >
              <Icon name="trash alternate outline" />
            </button>
          }
        />
      ) : (
        <Popup
          content={t("app.projects.reactivateItem")}
          trigger={
            <button
              type="button"
              className="project-card__action-btn"
              onClick={(e) => {
                stop(e);
                handleReactivate(row);
              }}
              aria-label={t("app.projects.reactivateItem")}
            >
              <Icon name="undo" />
            </button>
          }
        />
      )}
    </div>
  );

  const renderProjectCard = (row) => {
    const displayTitle = getProjectDisplayTitle(row) || "—";
    const descDisplay = getProjectDisplayDescription(row) || t("app.projects.noDescription");
    const pres = getProjectStatusPresentation(row, t);
    const pri = getPriorityPresentation(row, t);
    const pct = getTimelinePercentFromRow(row);
    const employees = getProjectAssociatedCount(row);
    const clientStr = String(row.clientName ?? row.ClientName ?? "").trim();
    const isInternal = row.isInternal ?? row.IsInternal;
    const createdDateStr = formatProjectCreatedDate(row);
    const rowKey = row.id ?? row.Id;

    const stop = (e) => e.stopPropagation();
    const inactiveClass = row.isActive === false ? "project-card--inactive" : "";
    const listMode = viewMode === "list";

    const commonArticle = (children) => (
      <article
        key={rowKey}
        role="button"
        tabIndex={0}
        className={`project-card project-card--clickable ${inactiveClass} ${listMode ? "project-card--list" : ""}`}
        onClick={() => handleEdit(row)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleEdit(row);
          }
        }}
        aria-label={`${t("app.projects.editItem")}: ${displayTitle}`}
      >
        {children}
      </article>
    );

    if (listMode) {
      return commonArticle(
        <>
          <div className="project-card__list-row">
            <div className="project-card__list-lead">
              <div className="project-card__icon-wrap" aria-hidden>
                <Icon name={DEFAULT_PROJECT_CARD_ICON} />
              </div>
              <div className="project-card__list-text">
                <h3 className="project-card__title">{displayTitle}</h3>
                <p className="project-card__list-client-line">
                  {clientStr ? (
                    <span className="project-card__client-name" title={clientStr}>
                      {clientStr}
                    </span>
                  ) : isInternal === true ? (
                    <span className="project-card__client-internal">{t("app.projects.cardInternalProjectReadable")}</span>
                  ) : (
                    <span className="project-card__client-empty">{t("app.projects.cardNoClientReadable")}</span>
                  )}
                </p>
              </div>
            </div>
            <p className="project-card__list-desc">{descDisplay}</p>
            <div className="project-card__list-center">
              {createdDateStr ? (
                <span className="project-card__list-created" title={t("app.projects.createdDate")}>
                  <Icon name="calendar outline" aria-hidden />
                  {createdDateStr}
                </span>
              ) : (
                <span className="project-card__list-created project-card__list-created--muted">{t("app.projects.na")}</span>
              )}
              <div className="project-card__list-progress">
                <div className="project-card__progress-track">
                  <div
                    className={`project-card__progress-fill ${pct === 0 ? "project-card__progress-fill--muted" : ""}`}
                    style={{ width: `${pct}%` }}
                  />
                </div>
                <span className="project-card__list-pct">{pct}%</span>
              </div>
            </div>
            <div className="project-card__list-end">
              {pri && (
                <Label color={pri.color} size="tiny" className="project-card__badge project-card__priority-label">
                  {pri.label}
                </Label>
              )}
              <div className="project-card__footer-team" title={t("app.projects.associatedEmployees")}>
                <Icon name="users" className="project-card__footer-team-icon" />
                <span className="project-card__footer-team-count">{employees}</span>
              </div>
              <Label color={pres.color} size="tiny" className="project-card__badge project-card__footer-status">
                {pres.label}
              </Label>
              {renderProjectActions(row, stop)}
            </div>
          </div>
        </>
      );
    }

    return commonArticle(
      <>
        <div className="project-card__top">
          <div className="project-card__top-main">
            <div className="project-card__icon-wrap" aria-hidden>
              <Icon name={DEFAULT_PROJECT_CARD_ICON} />
            </div>
            <div className="project-card__title-block">
              <h3 className="project-card__title">{displayTitle}</h3>
              {createdDateStr ? (
                <p className="project-card__created-meta">{t("app.projects.cardCreatedMeta", { date: createdDateStr })}</p>
              ) : null}
            </div>
          </div>
          <div className="project-card__top-aside">
            {pri && (
              <Label color={pri.color} size="tiny" className="project-card__badge project-card__priority-label">
                {pri.label}
              </Label>
            )}
            {renderProjectActions(row, stop)}
          </div>
        </div>

        <p className="project-card__desc">{descDisplay}</p>

        <p className="project-card__client-line">
          {clientStr ? (
            <>
              <span className="project-card__client-prefix">{t("app.projects.cardClientReadablePrefix")}</span>
              <span className="project-card__client-name" title={clientStr}>
                {clientStr}
              </span>
            </>
          ) : isInternal === true ? (
            <span className="project-card__client-internal">{t("app.projects.cardInternalProjectReadable")}</span>
          ) : (
            <span className="project-card__client-empty">{t("app.projects.cardNoClientReadable")}</span>
          )}
        </p>

        <div className="project-card__progress-wrap">
          <div className="project-card__progress-label">
            <span>{t("app.projects.cardTimeline")}</span>
            <span>{pct}%</span>
          </div>
          <div className="project-card__progress-track">
            <div
              className={`project-card__progress-fill ${pct === 0 ? "project-card__progress-fill--muted" : ""}`}
              style={{ width: `${pct}%` }}
            />
          </div>
        </div>

        <div className="project-card__footer">
          <div className="project-card__footer-team" title={t("app.projects.associatedEmployees")}>
            <Icon name="users" className="project-card__footer-team-icon" />
            <span className="project-card__footer-team-count">{employees}</span>
          </div>
          <div className="project-card__footer-status-wrap">
            <Label color={pres.color} size="tiny" className="project-card__badge project-card__footer-status">
              {pres.label}
            </Label>
          </div>
        </div>
      </>
    );
  };

  return (
    <div className="documents-page projects-settings-page container-fluid px-3 px-md-4 py-4">
      <CommonPageHeader
        barClassName="documents-header-bar"
        contentClassName="documents-header-content"
        leftClassName="documents-header-left"
        iconWrapperClassName="documents-header-icon-wrapper"
        textClassName="documents-header-text"
        titleClassName="documents-page-title"
        descriptionClassName="documents-page-description"
        rightClassName="documents-header-right"
        icon={<Icon name="briefcase" className="m-0" />}
        title={t("app.projects.title")}
        description={t("app.projects.description")}
        rightContent={
          <>
            <div className="common-page-header-stat-card" title={t("app.projects.statTotalProjects")}>
              <span className="common-page-header-stat-label">{t("app.projects.statTotalProjects")}</span>
              <span className="common-page-header-stat-value">{displayTotalCount}</span>
            </div>
            <Button primary onClick={openCreatePage} className="projects-add-btn">
              <Icon name="plus" />
              {t("app.projects.addItem")}
            </Button>
          </>
        }
      />
      {Error && (
        <Message negative>
          <Message.Header>{t("app.projects.errorHeader")}</Message.Header>
          <p>{message}</p>
        </Message>
      )}
      <div className="documents-list-content">
        <div className="department-list-description">
          {isLoading ? (
            <div className="loading-container">
              <Icon name="spinner" loading size="large" />
              <p>{t("app.projects.loading")}</p>
            </div>
          ) : totalItems === 0 ? (
            <div className="empty-state projects-empty-state projects-empty-state--initial">
              <div className="projects-empty-state__visual" aria-hidden>
                <Icon name="briefcase" size="huge" />
              </div>
              <h3>{t("app.projects.emptyTitle")}</h3>
              <p>{t("app.projects.emptyStateDescription")}</p>
              <Button primary className="projects-empty-state__cta" type="button" onClick={openCreatePage}>
                <Icon name="plus" />
                {t("app.projects.emptyAddProject")}
              </Button>
            </div>
          ) : (
            <div className="projects-grid-wrap table-container">
              <section className="projects-summary-strip" aria-label={t("app.projects.statTotalProjects")}>
                <p className="projects-summary-strip__hint">{t("app.projects.summaryHint")}</p>
                <div className="projects-summary-strip__chips" role="toolbar">
                  <button
                    type="button"
                    className={`projects-summary-chip projects-summary-chip--progress ${statusFilter === "inProgress" ? "projects-summary-chip--selected" : ""}`}
                    onClick={() => handleSummaryStatusFilter("inProgress")}
                  >
                    <span className="projects-summary-chip__label">{t("app.projects.workflowStatusInProgress")}</span>
                    <span className="projects-summary-chip__value">{loadedStatusBreakdown.inProgress}</span>
                  </button>
                  <button
                    type="button"
                    className={`projects-summary-chip projects-summary-chip--completed ${statusFilter === "completed" ? "projects-summary-chip--selected" : ""}`}
                    onClick={() => handleSummaryStatusFilter("completed")}
                  >
                    <span className="projects-summary-chip__label">{t("app.projects.workflowStatusCompleted")}</span>
                    <span className="projects-summary-chip__value">{loadedStatusBreakdown.completed}</span>
                  </button>
                  <button
                    type="button"
                    className={`projects-summary-chip projects-summary-chip--hold ${statusFilter === "onHold" ? "projects-summary-chip--selected" : ""}`}
                    onClick={() => handleSummaryStatusFilter("onHold")}
                  >
                    <span className="projects-summary-chip__label">{t("app.projects.workflowStatusOnHold")}</span>
                    <span className="projects-summary-chip__value">{loadedStatusBreakdown.onHold}</span>
                  </button>
                  <button
                    type="button"
                    className={`projects-summary-chip projects-summary-chip--muted ${statusFilter === "deactivated" ? "projects-summary-chip--selected" : ""}`}
                    onClick={() => handleSummaryStatusFilter("deactivated")}
                  >
                    <span className="projects-summary-chip__label">{t("app.projects.statusDeactivated")}</span>
                    <span className="projects-summary-chip__value">{loadedStatusBreakdown.deactivated}</span>
                  </button>
                  {loadedStatusBreakdown.other > 0 ? (
                    <div className="projects-summary-chip projects-summary-chip--static" title={t("app.projects.summaryOtherTitle")}>
                      <span className="projects-summary-chip__label">{t("app.projects.summaryOther")}</span>
                      <span className="projects-summary-chip__value">{loadedStatusBreakdown.other}</span>
                    </div>
                  ) : null}
                </div>
              </section>

              <div className="documents-filter-bar department-grid-toolbar projects-toolbar">
                <div className="documents-filter-bar-left">
                  <SearchInput
                    value={searchInput}
                    onChange={(next) => {
                      setSearchInput(next);
                    }}
                    onClear={() => {
                      setSearchInput("");
                    }}
                    placeholder={t("app.projects.searchPlaceholder")}
                    ariaLabel={t("app.projects.searchPlaceholder")}
                    wrapperClassName="documents-filter-search-wrap"
                    inputClassName="documents-input documents-filter-search-input"
                    iconClassName="bi bi-search documents-filter-search-icon"
                    clearButtonClassName="documents-filter-search-clear"
                  />
                  <SelectFilter
                    value={statusFilter}
                    onChange={(next) => {
                      setStatusFilter(next);
                    }}
                    options={statusFilterOptions}
                    ariaLabel={t("app.projects.filterAriaStatus")}
                    wrapperClassName="documents-filter-office"
                    iconClassName="bi bi-funnel documents-filter-office-icon"
                    selectClassName="documents-input documents-filter-select"
                  />
                  <SelectFilter
                    value={sortValue}
                    onChange={handleSortChange}
                    options={sortOptions}
                    ariaLabel={t("app.projects.sortBy")}
                    wrapperClassName="documents-filter-office projects-filter-sort"
                    iconClassName="bi bi-sort-down documents-filter-office-icon"
                    selectClassName="documents-input documents-filter-select"
                  />
                  {totalFiltered > 0 ? (
                    <span className="projects-results-badge">{t("app.projects.resultsShown", { count: totalFiltered })}</span>
                  ) : null}
                </div>
                <div className="documents-filter-bar-right projects-toolbar__right">
                  <div className="projects-view-toggle" role="group" aria-label={t("app.projects.viewToggleGroup")}>
                    <button
                      type="button"
                      className={`projects-view-toggle__btn ${viewMode === "grid" ? "projects-view-toggle__btn--on" : ""}`}
                      onClick={() => setViewMode("grid")}
                      aria-pressed={viewMode === "grid"}
                      title={t("app.projects.viewAsGrid")}
                    >
                      <Icon name="th" size="large" />
                      <span className="projects-view-toggle__sr">{t("app.projects.viewAsGrid")}</span>
                    </button>
                    <button
                      type="button"
                      className={`projects-view-toggle__btn ${viewMode === "list" ? "projects-view-toggle__btn--on" : ""}`}
                      onClick={() => setViewMode("list")}
                      aria-pressed={viewMode === "list"}
                      title={t("app.projects.viewAsList")}
                    >
                      <Icon name="list ul" />
                      <span className="projects-view-toggle__sr">{t("app.projects.viewAsList")}</span>
                    </button>
                  </div>
                  <ExcelExportButton
                    className="documents-compliance-export-btn projects-export-btn"
                    onClick={handleExport}
                    loading={exportLoading}
                    disabled={exportLoading}
                    title={t("app.projects.exportCsvTitle")}
                    label={t("app.projects.exportCsvLabel")}
                    loadingLabel={t("app.projects.exportCsvLoading")}
                  />
                </div>
              </div>

              {filteredItems.length === 0 ? (
                <div className="projects-empty-filter">
                  <Icon name="filter" size="large" color="grey" />
                  <h3>{t("app.projects.noFilterMatch")}</h3>
                  <p>{t("app.projects.noFilterMatchHint")}</p>
                </div>
              ) : (
                <>
                  <div className={`projects-grid ${viewMode === "list" ? "projects-grid--list-mode" : ""}`}>
                    {sortedItems.map((row) => renderProjectCard(row))}
                  </div>
                  {isLoadingMore && (
                    <div className="projects-infinite-loading" role="status" aria-live="polite">
                      <Icon name="spinner" loading />
                      <span>{t("app.projects.loadingMore")}</span>
                    </div>
                  )}
                  <div
                    ref={scrollSentinelRef}
                    className="projects-scroll-sentinel"
                    aria-hidden
                  />
                  <div className="documents-pagination documents-pagination--infinite">
                    <span className="documents-pagination-total">{paginationSummary}</span>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>
      <Confirm
        open={isDeleteModalOpen}
        header={t("app.projects.confirmDeactivate")}
        content={t("app.projects.confirmDeactivateMessage", { name: deletingItem?.name || "" })}
        cancelButton={t("app.common.cancel")}
        confirmButton={t("app.projects.confirmDeactivateButton")}
        onCancel={() => {
          setIsDeleteModalOpen(false);
          setDeletingItem(null);
        }}
        onConfirm={() => {
          if (deletingItem) {
            handleDelete(deletingItem);
            setIsDeleteModalOpen(false);
            setDeletingItem(null);
          }
        }}
        size="small"
        className="delete-confirm-modal"
        dimmer={{ className: "department-modal-dimmer" }}
      />
    </div>
  );
};

export default Project;
